import type { ButtonConfig } from '../../declare'
import { useEffect, useRef, useContext } from 'react'
import { autorun, toJS } from 'mobx'
import { Button } from 'antd'
import {
  EasyIcon,
  FormPropsContext,
  EditPropsContext,
  EditContext,
  TreePropsContext,
  TreeContext,
  EditTreeContext,
  ModalContext,
  ListPropsContext,
  ListTableContext,
  postMsg
} from 'src/components'
import { appStore, listStore } from 'src/stores'
import { $api, $consts } from 'src/plugins'
import { omitObjectFromData } from 'src/components/root/helper'
import { guid } from 'src/utils/tools'
import { set, get } from 'lodash'
import dayjs from 'dayjs'
import { unflatten } from 'flat'

type Props = {
  config: ButtonConfig
}

const SaveBtn = (props: Props) => {
  const formProps = useContext(FormPropsContext)
  const editProps = useContext(EditPropsContext)
  const edit = useContext(EditContext)
  const treeProps = useContext(TreePropsContext)
  const tree = useContext(TreeContext)
  const editTree = useContext(EditTreeContext)
  const listProps = useContext(ListPropsContext)
  /**
   * 如果是直接从 ListTable 打开的编辑表单，则 ListTableContext 能拿到，这是大多数的情况
   * 如果从 ListPage 列表表单表格头部按钮打开的编辑表单，则 ListTableContext 不能直接拿到，需要通过 listStore 来拿
   */
  const table = useContext(ListTableContext) || listStore.table.get(listProps.formCode)
  const modal = useContext(ModalContext)

  const fromEditRow = useRef()
  const fromListRow = useRef()
  useEffect(() => autorun(() => {
    const fromWhere = formProps.formData?.fromWhere

    /**
     * 编辑表单子表行配置按钮->ListPage->EditPage
     * 当前编辑表单的调用路径为：编辑表单的子表行按钮配置列表表单，列表又配置了另一个编辑表单，该编辑表单上的保存按钮
     * 需要关联 编辑表单子表行 自定义参数
     */
    const matchEditRow = (
      fromWhere?.pageType === $consts['TABLE/FORM_TYPE_EDIT_PAGE']
      && fromWhere?.locateType === $consts['TABLE/CONTROL_LOCATE_ROW']
      && fromWhere?.data
    )
    matchEditRow && (fromEditRow.current = matchEditRow)

    /**
     * 列表表单行配置按钮->ListPage->EditPage
     * 当前列表表单的调用路径为：列表表单的行按钮配置列表表单，列表又配置了另一个编辑表单，该编辑表单上的保存按钮
     * 需要关联 列表表单行按钮 自定义参数
     */
    const matchListRow = (
      fromWhere?.pageType === $consts['TABLE/FORM_TYPE_LIST_PAGE']
      && fromWhere?.locateType === $consts['TABLE/BTN_LOCATE_ROW']
      && fromWhere?.data
    )
    matchListRow && (fromListRow.current = matchListRow)
  }), [])

  // 批量保存
  const handleBatchSave = () => {
    const copyFormBase = toJS(edit.formBase)

    const { formBatch, batchData, validNavColumns } = edit
    const editFormBase = {}
    validNavColumns.forEach((col: any) => {
      const value = get(copyFormBase, col.FIELD_NAME)
      // value !== undefined && set(editFormBase, col.FIELD_NAME, value)
      // 这边不用 set，是为了保持 Nav.Field 的 key 值
      value !== undefined && Object.assign(editFormBase, { [col.FIELD_NAME]: value })
    })

    if (!Object.keys(editFormBase).length) {
      postMsg('未检测到变更', 'info')
      return
    }

    const copyData = toJS(batchData)
    copyData.forEach((data: any) => {
      for (const [key, value] of Object.entries(editFormBase) as any) {
        const navColumn = validNavColumns.find((col: any) => col.FIELD_NAME === key)

        const currValue = get(data, key)
        const operator = formBatch[key] || $consts['EDIT/BATCH_TEXTINPUT_UPDATE']
        switch (operator) {
          case $consts['EDIT/BATCH_TEXTINPUT_UPDATE']: // 修改为
            set(data, key, value)
            if (navColumn.CONTROL_TYPE === $consts['TABLE/CONTROL_TYPE_PAGINATION_SELECT']) {
              const saveFields = navColumn.INPUT_SAVE_FIELD.split(',')
              saveFields.forEach((field: string) => {
                set(data, field, get(copyFormBase, field))
              })
              navColumn.INPUT_ID_FIELD && set(data, navColumn.INPUT_ID_FIELD, get(copyFormBase, 'ID'))
            }
            break
          case $consts['EDIT/BATCH_TEXTINPUT_CLEAR']: // 清空
            switch (navColumn.CONTROL_TYPE) {
              case $consts['TABLE/CONTROL_TYPE_ENUM_SELECT']:
              case $consts['TABLE/CONTROL_TYPE_NUMBERINPUT']:
                set(data, key, 0)
                break
              case $consts['TABLE/CONTROL_TYPE_SWITCH']:
              case $consts['TABLE/CONTROL_TYPE_CHECKBOX']:
                set(data, key, false)
                break
              case $consts['TABLE/CONTROL_TYPE_PAGINATION_SELECT']:
                navColumn.INPUT_ID_FIELD && set(data, navColumn.INPUT_ID_FIELD, null)
                break
              default:
                set(data, key, null)
                break
            }
            break
          case $consts['EDIT/BATCH_TEXTINPUT_ADD_BEFORE']: // 在开始添加
            set(data, key, `${value}${currValue}`)
            break
          case $consts['EDIT/BATCH_TEXTINPUT_ADD_AFTER']: // 在结束添加
            set(data, key, `${currValue}${value}`)
            break
          case $consts['EDIT/BATCH_TEXTINPUT_REPLACE']: // 替换
            {
              const replaceValue = get(formBatch, `${key}__REPLACE__`)
              set(data, key, String(currValue).replace(new RegExp(replaceValue, 'g'), value))
            }
            break
          case $consts['EDIT/BATCH_DATE_DAY_PLUS_DAY']: // 增加天数
            {
              const replaceValue = get(formBatch, `${key}__REPLACE__`)
              const targetDate = dayjs(value).add(replaceValue, 'days').format()
              set(data, key, targetDate)
            }
            break
          case $consts['EDIT/BATCH_DATE_DAY_PLUS_MONTH']: // 增加月数
            {
              const replaceValue = get(formBatch, `${key}__REPLACE__`)
              const targetDate = dayjs(value).add(replaceValue, 'months').format()
              set(data, key, targetDate)
            }
            break
          case $consts['EDIT/BATCH_DATE_DAY_PLUS_YEAR']: // 增加年数
            {
              const replaceValue = get(formBatch, `${key}__REPLACE__`)
              const targetDate = dayjs(value).add(replaceValue, 'years').format()
              set(data, key, targetDate)
            }
            break
          case $consts['EDIT/BATCH_DATE_DAY_SUB_DAY']: // 减少天数
            {
              const replaceValue = get(formBatch, `${key}__REPLACE__`)
              const targetDate = dayjs(value).subtract(replaceValue, 'days').format()
              set(data, key, targetDate)
            }
            break
          case $consts['EDIT/BATCH_DATE_DAY_SUB_MONTH']: // 减少月数
            {
              const replaceValue = get(formBatch, `${key}__REPLACE__`)
              const targetDate = dayjs(value).subtract(replaceValue, 'months').format()
              set(data, key, targetDate)
            }
            break
          case $consts['EDIT/BATCH_DATE_DAY_SUB_YEAR']: // 减少年数
            {
              const replaceValue = get(formBatch, `${key}__REPLACE__`)
              const targetDate = dayjs(value).subtract(replaceValue, 'years').format()
              set(data, key, targetDate)
            }
            break
        }
      }
    })

    const { Nav_PageEdit } = edit.editConfig
    const { Nav_PageEdit: deepConfig } = Nav_PageEdit
    $api['app/getDataByPost'](
      { Data: deepConfig.IS_CONTAINS_NAV ? unflatten(copyData) : omitObjectFromData(copyData) },
      { url: deepConfig.BATCH_UPDATE_API }
    ).then((res: any) => {
      if (res) {
        postMsg('操作成功')
        table?.reload()
        modal?.close()
      }
    })
  }

  // 保存
  const handleSave = () => {
    const batchEdit = editProps.formData?.batchEdit
    // 表单校验
    const isInvalid = edit?.checkVerifyIsInvalid()
    if (isInvalid && !batchEdit) {
      isInvalid && postMsg('表单输入有误，请检查必填项', 'error')
      return
    }

    // 批量编辑保存
    if (batchEdit) {
      handleBatchSave()
      return
    }

    const copyFormBase = toJS(edit.formBase)

    // 子表数据
    const tableData = edit.editPageTable.saveTableData ?? {}
    for (const [key, value] of Object.entries(tableData)) {
      const saveData: any = toJS(value)
      saveData.forEach((data: any) => {
        // 移除 __ 开头的前端本地数据以及带 . 的导航属性数据
        Object.keys(data)
          .filter(dkey => dkey.startsWith('__') || dkey.indexOf('.') !== -1)
          .forEach(dkey => { delete data[dkey] })
      })
      set(copyFormBase, key, saveData)
    }

    // 复制新增
    const copyEdit = editProps.formData?.copyEdit
    copyEdit && Object.assign(copyFormBase, {
      ID: guid(),
      RecordSelected: editProps.formData?.formId
    })

    const { Nav_PageEdit } = edit.editConfig
    const { Nav_PageEdit: deepConfig } = Nav_PageEdit

    // 树节点配置
    if (deepConfig.TREE_SELECTED_SET_FIELD && tree.selectedKeys?.length) {
      set(copyFormBase, deepConfig.TREE_SELECTED_SET_FIELD, tree.selectedKeys[0])
    }

    // 额外需要保存的参数
    treeProps.formData?.extraSaveData && Object.assign(copyFormBase, treeProps.formData.extraSaveData)

    if (deepConfig.API_URL) {
      const { OrgId: ORG_ID } = appStore.current.headers
      const payload = { ORG_ID, ...copyFormBase }
      Object.assign(payload, fromListRow.current, fromEditRow.current)

      $api['app/getDataByPost'](
        deepConfig.IS_CONTAINS_NAV ? unflatten(payload) : omitObjectFromData(payload),
        { url: deepConfig.API_URL }
      ).then((res: any) => {
        if (res) {
          postMsg('操作成功')
          table?.reload()
          // 如果是编辑树表单，则更新编辑树数据
          const parentTree = editTree?.flatTreeData.find((item: any) => item.ID === editTree.selectedNodes[0].PARENT_ID)
          editTree?.getTreeData(parentTree)
          // 用于一些弹窗关闭需要回调的场景用
          modal?.close(copyFormBase)
        }
      })
    } else {
      postMsg('未配置对应接口，请检查配置', 'error')
    }
  }

  const { __READONLY__, LABEL, ICON } = props.config
  return (
    <Button
      type='primary'
      disabled={__READONLY__}
      icon={<EasyIcon type={ICON} defaultIcon='SaveOutlined' />}
      onClick={handleSave}
    >{LABEL}</Button>
  )
}

export default SaveBtn